import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Building2, MessageSquareText, Star } from "lucide-react";
import SeoHead from "./SiteSeo";
import { WinningWord } from "./sections";
import { LOGO } from "./nav";
import { trackBoth } from "../lib/metaEvent";
import BookingWidget from "./booking/BookingWidget";
import { useReviewsLabel } from "./Testimonials";
import "./site.css";

export const BOOK_CALENDLY_URL = "https://calendly.com/mattbroomhall/consultation";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const viewed = new Set();  // StrictMode re-runs effects in dev; one ViewContent per visit

export const readUtm = (search) => {
  const q = new URLSearchParams(search);
  const utm = {};
  UTM_KEYS.forEach((k) => { const v = q.get(k); if (v) utm[k] = v; });
  return utm;
};

// Shared chrome for the ad landing pair (/book/ + /booked/): logo only, no nav, no competing CTAs.
export function BookShell({ children, testId }) {
  return (
    <div className="hm-site book-page" data-testid={testId}>
      <header className="book-header">
        <div className="wrap"><Link to="/" aria-label="Hello Mortgage Team home" data-testid="book-logo-link"><img className="logo" src={LOGO} alt="Hello Mortgage Team" /></Link></div>
      </header>
      {children}
      <footer className="book-footer" data-testid="book-footer">
        <div className="wrap book-footer-row">
          <span>© {new Date().getFullYear()} Hello Mortgage Team · Powered by BRX Mortgage</span>
        </div>
      </footer>
    </div>
  );
}

export default function BookPage() {
  const { search } = useLocation();
  const reviews = useReviewsLabel();
  const q = new URLSearchParams(search);
  const [ctx, setCtx] = useState(null);
  const [first, setFirst] = useState(null);
  const [override, setOverride] = useState(q.get("context") || null);
  const fromPath = q.get("from") || null;

  useEffect(() => {
    if (!viewed.has(search)) { viewed.add(search); trackBoth("ViewContent", { content_name: "book_call" }); }
  }, [search]);

  return (
    <BookShell testId="book-page">
      <SeoHead title="Book a 15-minute mortgage call — Hello Mortgage Team" description="Let’s figure out your winning move. 15 minutes. No pressure. Just clear answers and a plan for what comes next. Pick a time that works and we'll call you." path="/book/" />
      <main className="book-main book2-main">
        <div className="wrap book2-grid">
          <aside className="book2-left" data-testid="book-aside">
            <div>
              <div className="eyebrow">{ctx?.key ? `${ctx.label} · ${ctx.duration}-minute call` : "15-minute call"}</div>
              <h1 className="display" data-testid="book-h1">{first ? `${first}, let’s` : "Let’s"} figure out your <WinningWord word="winning" storageKey="hm_book_win" /> move.</h1>
              <p className="book-sub" data-testid="book-subhead">No pressure. Just clear answers. Pick a time — we’ll call you.</p>
            </div>
            <div className="book2-team" data-testid="book-team-card"><b>Hello Mortgage Team</b><span>Independent brokers · Licensed in Alberta (RECA) · Powered by BRX Mortgage</span></div>
            <div className="book2-trust" data-testid="book-trust">
              <div><Building2 size={20} aria-hidden="true" /><span>Compare 50+ lenders</span></div>
              <div><MessageSquareText size={20} aria-hidden="true" /><span>Clear advice. Zero mortgage-speak.</span></div>
              <div><Star size={20} aria-hidden="true" /><span>{reviews} five-star reviews</span></div>
            </div>
            {ctx?.contexts && (
              <div className="book2-switch" data-testid="context-switch" aria-label="What's the call about?">
                {ctx.contexts.map((c) => <button type="button" key={c.key} className={c.key === ctx.key ? "on" : ""} onClick={() => setOverride(c.key)} data-testid={`context-${c.key}`}>{c.label}</button>)}
              </div>
            )}
          </aside>
          <section className="book2-card" data-testid="book-widget-card">
            <BookingWidget key={override || fromPath || "default"} path={fromPath} contextKey={override} onContext={setCtx} token={q.get("t")} onKnown={(k) => setFirst(k?.name ? k.name.split(" ")[0] : null)} />
            <noscript><a href={BOOK_CALENDLY_URL}>Pick a time on Calendly</a></noscript>
          </section>
        </div>
      </main>
    </BookShell>
  );
}
